<script setup>
import axios from 'axios';
import { BAlert, BButton, BForm, BFormGroup, BFormTextarea } from 'bootstrap-vue-next';
import { computed, ref } from 'vue';

const props = defineProps({
  username: { type: String, required: true },
  hackathon: { type: Object, required: true },
});
const emit = defineEmits(['changed']);

const reason = ref('');
const reasonInvalid = ref(false);
const reasonState = computed(() => (reasonInvalid.value ? false : null));
const submitting = ref(false);
const showReinstateConfirm = ref(false);
const errorMessage = ref(null);

const url = () => `/api/v1/users/${props.username}/hackathon_disqualification`;

const request = async (action) => {
  if (submitting.value) {
    return;
  }

  submitting.value = true;
  errorMessage.value = null;

  try {
    await action();
  } catch {
    errorMessage.value = 'Something went wrong and the change was not saved. Please try again.';
  } finally {
    submitting.value = false;
  }
};

const validateReason = () => {
  reasonInvalid.value = reason.value.trim() === '';

  return !reasonInvalid.value;
};

const disqualify = () => {
  if (!validateReason()) {
    return;
  }

  request(async () => {
    const { data } = await axios.post(url(), { hackathon_id: props.hackathon.id, reason: reason.value.trim() });
    emit('changed', data);
  });
};

const reinstate = () => {
  if (!showReinstateConfirm.value) {
    showReinstateConfirm.value = true;

    return;
  }

  request(async () => {
    await axios.delete(url(), { params: { hackathon_id: props.hackathon.id } });
    showReinstateConfirm.value = false;
    reason.value = '';
    emit('changed', null);
  });
};

const cancelReinstate = () => {
  showReinstateConfirm.value = false;
};
</script>

<template>
  <div class="mt-5 pt-4 border-top">
    <h2 class="h4">
      {{ hackathon.display_name }} hackathon
    </h2>
    <p v-if="hackathon.registered">
      Has accepted the hackathon rules (version {{ hackathon.terms_version }}).
    </p>
    <p v-else>
      Has not accepted the hackathon rules yet.
    </p>
    <BAlert
      :model-value="errorMessage != null"
      variant="danger"
      class="mb-3"
    >
      {{ errorMessage }}
    </BAlert>
    <template v-if="hackathon.disqualification">
      <p class="text-danger">
        Disqualified from this hackathon on {{ hackathon.disqualification.created_at.slice(0, 10) }}
        by {{ hackathon.disqualification.disqualified_by_username ?? 'a deleted account' }}.
      </p>
      <p>
        Reason: {{ hackathon.disqualification.reason }}
      </p>
      <div class="d-flex gap-2">
        <BButton
          type="button"
          variant="secondary"
          @click="reinstate"
        >
          {{ showReinstateConfirm ? 'Confirm reinstate' : 'Reinstate' }}
        </BButton>
        <BButton
          v-if="showReinstateConfirm"
          type="button"
          variant="outline-secondary"
          @click="cancelReinstate"
        >
          Cancel
        </BButton>
      </div>
    </template>
    <BForm
      v-else
      @submit.prevent="disqualify"
    >
      <BFormGroup
        label="Reason for disqualification"
        invalid-feedback="A reason is required."
        :state="reasonState"
        class="mb-3"
      >
        <BFormTextarea
          v-model="reason"
          rows="3"
          required
          :state="reasonState"
        />
      </BFormGroup>
      <BButton
        type="submit"
        variant="danger"
      >
        Disqualify
      </BButton>
      <small class="text-muted d-block mt-2">
        Removes the contributor from the leaderboard for this hackathon only. They see a notice, not the reason.
      </small>
    </BForm>
  </div>
</template>
